import { formatCaregiverLocation } from "@utils/helpers";
import { format } from "date-fns";
import React from "react";
import { useNavigate } from "react-router-dom";

import { Column, Headline, Table } from "@fronterahealth/frontera-ui-components";

import { useGetClinicLeadsQuery } from "@api/graphql/types-and-hooks";
import { convertReadableString } from "@components/forms/utils";

interface Lead {
  id: string;
  parentName: string;
  requestedServices: string;
  receivedDate: string;
  location: string;
}

const tableColumns: Column<Lead>[] = [
  {
    title: "Parent Name",
    dataIndex: "parentName",
    key: "parentName",
  },
  {
    title: "Service",
    dataIndex: "requestedServices",
    key: "requestedServices",
  },
  {
    title: "Received",
    dataIndex: "receivedDate",
    key: "receivedDate",
  },
  {
    title: "Location",
    dataIndex: "location",
    key: "location",
  },
];

export const Leads: React.FC = () => {
  const navigate = useNavigate();

  const { data, isLoading } = useGetClinicLeadsQuery({ filter: {} }, { queryKey: ["getClinicLeads"] });

  const leads: Lead[] =
    data?.clinicLeads?.edges
      ?.flatMap((edge) => (edge?.node ? [edge.node] : []))
      .map((node) => {
        const caregiverMetaData = node.caregiver?.caregivermetadata;

        const parentName = caregiverMetaData
          ? `${caregiverMetaData.firstName} ${caregiverMetaData.lastName}`.trim()
          : "—";

        const parentLocation = caregiverMetaData ? formatCaregiverLocation(caregiverMetaData) || "—" : "—";

        return {
          id: node.id,
          parentName,
          requestedServices: node.requestedServiceTypes?.map(convertReadableString).join(", ") ?? "—",
          receivedDate: format(new Date(node.createdDate), "MMM d, yyyy"),
          location: parentLocation,
        };
      }) ?? [];

  return (
    <div className="flex flex-col gap-4 mt-6">
      <Headline size="medium">Leads</Headline>
      <Table
        cellClassName="bg-white"
        inputClassName="bg-white"
        data={leads}
        columns={tableColumns}
        loading={isLoading}
        emptyMessage="No leads yet"
        showPagination
        showSearch
        defaultItemsPerPage={20}
        onRowClick={(data) => navigate(`/leads/${data.id}`)}
      />
    </div>
  );
};
